import { useEffect, useRef, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

/**
 * Apparition au défilement.
 *
 * Trois précautions, parce qu'une animation ratée coûte plus cher qu'une
 * animation absente :
 *
 * 1. **Une seule fois.** L'observateur se débranche dès que l'élément est
 *    apparu. Un contenu qui refait son entrée à chaque passage donne le
 *    mal de mer et empêche de relire un paragraphe.
 * 2. **Repli sûr.** Sans `IntersectionObserver`, on révèle immédiatement.
 *    Mieux vaut perdre l'effet que perdre la page.
 * 3. **Rien à annoncer.** L'effet est purement visuel : aucun attribut ARIA,
 *    le contenu est dans le DOM dès le premier rendu et reste lisible par
 *    un lecteur d'écran comme par un moteur de recherche.
 */
export function Reveal({
  children,
  delay = 0,
  className,
  as: Tag = 'div',
}: {
  children: ReactNode
  /** Décalage en millisecondes — sert à faire entrer une grille en cascade. */
  delay?: number
  className?: string
  as?: 'div' | 'li' | 'section' | 'span'
}) {
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    if (typeof IntersectionObserver === 'undefined') {
      node.dataset.revealed = 'true'
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        node.dataset.revealed = 'true'
        observer.disconnect()
      },
      // Le seuil bas et la marge négative déclenchent quand l'élément est
      // franchement entré, pas quand son premier pixel affleure le bas de
      // l'écran — sinon l'animation est finie avant qu'on la voie.
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <Tag
      ref={ref as never}
      className={cn('reveal', className)}
      style={delay ? ({ '--reveal-delay': `${delay}ms` } as React.CSSProperties) : undefined}
    >
      {children}
    </Tag>
  )
}

/**
 * Halo qui suit le curseur.
 *
 * Les coordonnées sont écrites en variables CSS plutôt qu'en état React :
 * un `setState` à chaque mouvement de souris relancerait le rendu de tout
 * l'arbre trente fois par seconde, pour un effet purement décoratif.
 */
export function useSpotlight<T extends HTMLElement>() {
  const ref = useRef<T>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    // Un appareil tactile n'a pas de curseur à suivre : on s'abstient plutôt
    // que de laisser un halo figé là où le doigt s'est posé.
    if (!window.matchMedia('(hover: hover)').matches) return

    function onMove(event: PointerEvent) {
      const bounds = node!.getBoundingClientRect()
      node!.style.setProperty('--mx', `${event.clientX - bounds.left}px`)
      node!.style.setProperty('--my', `${event.clientY - bounds.top}px`)
    }

    node.addEventListener('pointermove', onMove)
    return () => node.removeEventListener('pointermove', onMove)
  }, [])

  return ref
}
